import {
  AppBar,
  Box,
  IconButton,
  styled,
  type Theme,
  Toolbar,
  useMediaQuery,
  Autocomplete,
  Typography,
  Tooltip,
} from '@mui/material';
import { SettingsContext } from 'contexts/settingsContext';
import ThemeIcon from 'icons/ThemeIcon';
import { type FC, Fragment, useContext, useState, useEffect } from 'react';
import LanguagePopover from './popovers/LanguagePopover';
import ProfilePopover from './popovers/ProfilePopover';
import AppTextField from 'components/input-fields/AppTextField';
import { employeesApi, seasonApi, realAxios } from 'api';
import { SeasonEntity } from 'api/generated';
import { useSeason } from 'contexts/SeasonContext';
import jwtDecode from 'jwt-decode';
import { useTranslation } from 'react-i18next';
import NotificationsPopover from './popovers/NotificationsPopover';
import EncryptionPopover from './popovers/EncryptionPopover';
import HiddenPopover from './popovers/HiddenPopover';
import { checkTokenRole } from 'utils/checkToken';
import { isWithinInterval } from 'date-fns';


// ------------------------------------------------
interface DashboardHeaderProps {
  setShowSideBar: () => void;
  setShowMobileSideBar: () => void;
}

// ------------------------------------------------

// custom styled components
const DashboardHeaderRoot = styled(AppBar)(({ theme }) => ({
  zIndex: 11,
  boxShadow: 'none',
  paddingTop: '1rem',
  paddingBottom: '1rem',
  backdropFilter: 'blur(6px)',
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
}));

const StyledToolBar = styled(Toolbar)(() => ({
  '@media (min-width: 0px)': {
    paddingLeft: 0,
    paddingRight: 0,
    minHeight: 'auto',
  },
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  '&:hover': { backgroundColor: theme.palette.action.hover },
}));

const ToggleIcon = styled(Box)<{ width?: number }>(({ theme, width }) => ({
  height: 3,
  margin: '5px',
  marginLeft: 0,
  width: width || 25,
  borderRadius: '10px',
  transition: 'width 0.3s',
  backgroundColor: theme.palette.primary.main,
}));

const StatusDot = styled(Box)(({ theme }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.error.main,
  marginRight: 8,
  flexShrink: 0,
}));

const UnencryptedDot = styled(Box)(({ theme }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.warning.main,
  marginRight: 8,
  flexShrink: 0,
  cursor: 'pointer',
}));

const SeasonStatusLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.7rem',
  color: theme.palette.error.main,
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
  marginRight: 8,
}));

const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour
const INACTIVITY_CHECK_INTERVAL_MS = 60 * 1000; // check every minute
const INTERACTION_THROTTLE_MS = 30 * 1000; // update localStorage at most every 30s
const LAST_INTERACTION_KEY = 'onestaff_last_interaction';

const DashboardHeader: FC<DashboardHeaderProps> = (props) => {
  const { setShowMobileSideBar } = props;

  const [isSeasonsLoaded, setIsSeasonsLoaded] = useState(false);
  const [defaultSeason, setDefaultSeason] = useState<undefined | { label: string; id: number }>();
  const { seasons, setSeasons, seasonId, setSeasonId } = useSeason();
  const { settings, saveSettings } = useContext(SettingsContext);
  const downMd = useMediaQuery((theme: Theme) => theme.breakpoints.down(1200));
  const { t } = useTranslation();
  const [selectedSeason, setSelectedSeason] = useState<SeasonEntity | null>(null);
  const [unencryptedEmployees, setUnencryptedEmployees] = useState<Array<{id: number, firstName: string, lastName: string}>>([]);

  const handleChangeTheme = (value: 'light' | 'dark') => {
    saveSettings({ ...settings, theme: value });
  };

  const checkToken = () => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      const decodedToken = jwtDecode<{ employeeId: number }>(accessToken);
      return decodedToken.employeeId
    }
    throw new Error("No valid token found!");
  }

  const isSeasonActive = (season: SeasonEntity) => {
    if (!season.startDate || !season.endDate) return true;
    const today = new Date();
    const startDate = new Date(season.startDate);
    const endDate = new Date(season.endDate);
    return isWithinInterval(today, { start: startDate, end: endDate });
  };

  useEffect(() => {
    const findSeasonsByRole = async (role: string) => {
      switch (role) {
        case 'DEPCHIEF':
          return await seasonApi.findMySeasonInProgress();
        case 'USER':
          return await seasonApi.findMySeasonInProgress();
        default:
          return await seasonApi.findAll();
      }
    }

    const fetchSeasonData = async () => {
      const employeeId = checkToken();
      const role = checkTokenRole();
      const selectedSeason = await employeesApi.getSeason(employeeId);
      const { data } = await findSeasonsByRole(role);
      setSeasons([...data]);
      const defaultSeasonData = selectedSeason.data ? selectedSeason.data : data[0];
      setDefaultSeason({
        label: defaultSeasonData.name,
        id: defaultSeasonData.id
      });
      setSelectedSeason(defaultSeasonData);
      setSeasonId(defaultSeasonData.id);
      setIsSeasonsLoaded(true);
    };

    fetchSeasonData();

    const fetchUnencryptedEmployees = async () => {
      try {
        const role = checkTokenRole();
        if (role === 'MANAGER' || role === 'OWNER' || role === 'DEVELOPER' || role === 'ADMIN') {
          const response = await realAxios.get('/employee-note/unencrypted-employees');
          setUnencryptedEmployees(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch unencrypted employees', error);
      }
    };
    fetchUnencryptedEmployees();
  }, [seasonId]);

  // Track user interaction timestamps (throttled)
  useEffect(() => {
    let lastUpdate = 0;
    const onInteraction = () => {
      const now = Date.now();
      if (now - lastUpdate > INTERACTION_THROTTLE_MS) {
        lastUpdate = now;
        localStorage.setItem(LAST_INTERACTION_KEY, now.toString());
      }
    };

    // Initialize timestamp
    localStorage.setItem(LAST_INTERACTION_KEY, Date.now().toString());

    const events: Array<keyof WindowEventMap> = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, onInteraction, { passive: true }));

    return () => {
      events.forEach(e => window.removeEventListener(e, onInteraction));
    };
  }, []);

  // Auto-switch to active season after inactivity timeout
  useEffect(() => {
    if (!seasons.length || !isSeasonsLoaded) return;

    const checkInactivity = () => {
      const lastInteraction = parseInt(localStorage.getItem(LAST_INTERACTION_KEY) || '0', 10);
      if (Date.now() - lastInteraction < INACTIVITY_TIMEOUT_MS) return;

      // Already on an active season — nothing to do
      if (selectedSeason && isSeasonActive(selectedSeason)) return;

      const activeSeason = seasons.find(s => isSeasonActive(s));
      if (!activeSeason || activeSeason.id === seasonId) return;

      try {
        const employeeId = checkToken();
        setSeasonId(activeSeason.id);
        setSelectedSeason(activeSeason);
        setDefaultSeason({ label: activeSeason.name, id: activeSeason.id });
        employeesApi.setSeasonToDisplay({ employeeId, seasonId: activeSeason.id });
      } catch {
        // token missing — skip auto-switch
      }

      // Reset interaction timer so it doesn't fire again immediately
      localStorage.setItem(LAST_INTERACTION_KEY, Date.now().toString());
    };

    const intervalId = setInterval(checkInactivity, INACTIVITY_CHECK_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [seasons, seasonId, isSeasonsLoaded, selectedSeason]);

  return (
    <DashboardHeaderRoot position='sticky'>
      <StyledToolBar>
        {downMd && (
          <Box sx={{ cursor: 'pointer' }} onClick={setShowMobileSideBar}>
            <ToggleIcon />
            <ToggleIcon width={18} />
            <ToggleIcon width={9} />
          </Box>
        )}

        <Box flexGrow={1} ml={1} />
        {seasons.length > 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {selectedSeason && !isSeasonActive(selectedSeason) && (
              <Tooltip title={t('season.notActive')}>
                <SeasonStatusLabel>
                  <StatusDot />
                  {t('season.notActive')}
                </SeasonStatusLabel>
              </Tooltip>
            )}
            {unencryptedEmployees.length > 0 && (
              <Tooltip 
                title={
                  <Box>
                    <Typography variant="body2" fontWeight="bold">{t('encryption.unencryptedNotes')}</Typography>
                    {unencryptedEmployees.map(emp => (
                      <Typography key={emp.id} variant="caption" display="block">
                        • {emp.firstName} {emp.lastName}
                      </Typography>
                    ))}
                  </Box>
                }
              >
                <Box display="flex" alignItems="center">
                  <UnencryptedDot />
                </Box>
              </Tooltip>
            )}
            <Autocomplete
              key={defaultSeason?.id}
              defaultValue={isSeasonsLoaded ? defaultSeason : undefined}
              id="seasons-selector"
              disableClearable
              options={seasons?.map((season: SeasonEntity) => ({
                label: season.name,
                id: season.id
              })) || []}
              renderInput={(params) =>
                <AppTextField
                  {...params}
                  placeholder={t('season.seasonSelector.select')}
                  label={t('season.itemName')}
                  size='small'
                  sx={{
                    '.MuiInputBase-input': { fontSize: '0.7rem', fontWeight: '500' },
                    '#seasons-selector-label': { fontSize: '0.7rem', fontWeight: '500' },
                  }}
                />
              }
              ListboxProps={{ style: { maxHeight: 150 } }}
              onChange={async (event, value) => {
                const employeeId = checkToken();
                try {
                  await employeesApi.setSeasonToDisplay({ employeeId, seasonId: value.id });
                } catch (error) {
                  console.error('Failed to set season', error);
                }
                setSeasonId(value.id);
                const selectedSeasonData = seasons.find(s => s.id === value.id);
                setSelectedSeason(selectedSeasonData || null);
              }}
              style={{ width: '200px' }}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Typography fontSize={12}>
                    {option.label}
                  </Typography>
                </li>
              )}
              isOptionEqualToValue={(option, value) => option.label === value?.label}
            />
          </Box>
        ) : <></>}

        {settings.theme === 'light' ? (
          <StyledIconButton
            onClick={() => {
              handleChangeTheme('dark');
            }}
          >
            <ThemeIcon />
          </StyledIconButton>
        ) : (
          <StyledIconButton
            onClick={() => {
              handleChangeTheme('light');
            }}
          >
            <ThemeIcon />
          </StyledIconButton>
        )}

        <Fragment>
          <LanguagePopover />
        </Fragment>

        <NotificationsPopover />
        <EncryptionPopover />
        <HiddenPopover />
        <ProfilePopover />
      </StyledToolBar>
    </DashboardHeaderRoot>
  );
};

export default DashboardHeader;
